import { ADMIN_DEMO_STORAGE_KEY } from "core/adminDemoSelections";

import type { PolicyGateDiagramGate } from "../PolicyGatesDemo/types";

export { ADMIN_DEMO_STORAGE_KEY };

// ---------------------------------------------------------------------------
// Shared helpers for reading/writing fields inside the sb_admin_demo object.
// All demo pages should use these instead of separate localStorage keys.
// ---------------------------------------------------------------------------

function loadStore(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(ADMIN_DEMO_STORAGE_KEY);
    if (raw == null) return {};
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function persistStore(store: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ADMIN_DEMO_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // best-effort
  }
}

export function loadDemoField<T>(field: string, fallback: T): T {
  const value = loadStore()[field];
  if (value === undefined) return fallback;
  return value as T;
}

export function saveDemoField(field: string, value: unknown): void {
  const store = loadStore();
  store[field] = value;
  persistStore(store);
}

export function hasDemoField(field: string): boolean {
  return loadStore()[field] !== undefined;
}

export function removeDemoField(field: string): void {
  const store = loadStore();
  delete store[field];
  persistStore(store);
}

export function loadDemoAgents<T extends { id: string }>(
  field: string,
  fallback: T[],
): T[] {
  const value = loadDemoField<unknown>(field, null);
  if (!Array.isArray(value)) return fallback;
  const isAgentLike = (item: unknown): item is T =>
    typeof item === "object" && item != null && "id" in item;
  return value.every(isAgentLike) ? (value as T[]) : fallback;
}

export type PackageRegistryEntry = {
  id: string;
  fallbackEnabled: boolean;
  scope: string;
  status: "connected" | "error";
  url: string;
};

export type AdminDemoConfig = {
  analytics?: {
    configured: boolean;
    logoUrl?: string;
    vendorName: string;
  };
  clarkInference?: {
    providerId: string;
    providerName: string;
  };
  cognitoConfig?: {
    clientId: string;
    clientSecret: string;
    groupClaim: string;
    region: string;
    userPoolId: string;
  };
  externalAuth?: {
    providerId: string;
    providerName: string;
  };
  database?: {
    providerId: string;
    providerName: string;
  };
  databaseProd?: {
    providerId: string;
    providerName: string;
  };
  dbProvider?: {
    providerId: string;
    providerName: string;
  };
  fileStorage?: {
    providerId: string;
    providerName: string;
  };
  fileStorageProd?: {
    providerId: string;
    providerName: string;
  };
  fsProvider?: {
    providerId: string;
    providerName: string;
  };
  observability?: {
    configured: boolean;
    detail: string;
    logoUrl?: string;
    vendorName: string;
  };
  packageRegistries?: {
    configured: boolean;
    registries: PackageRegistryEntry[];
    registryCount: number;
  };
  platformMcp?: {
    configured: boolean;
  };
  policyGates?: {
    createdGates?: PolicyGateDiagramGate[];
    enabledById: Record<string, boolean>;
    enabledCount: number;
  };
  siem?: {
    logoUrl?: string;
    provider: string;
  };
};

export type AppAiOverride = {
  iconLocation?: string;
  integrationName: string;
  pluginId: string;
  pluginName: string;
};

export type RealPlatformData = {
  agentSkills?: { configuredCount: number; totalCount: number };
  appAi?: { configured: boolean; pluginId?: string; providerName: string };
  appAiAdvanced?: {
    imageGeneration?: AppAiOverride;
    speechToText?: AppAiOverride;
    textGeneration?: AppAiOverride;
  };
  dataPlanes?: { healthyCount: number; totalCount: number };
  spending?: { configured: boolean };
  usersGroups?: { groupCount: number; userCount: number };
  integrations?: {
    count: number;
    items: Array<{ id: string; name: string; pluginId: string }>;
  };
};
